import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, X, RotateCcw, PanelRight, PanelRightClose, ArrowRight, ExternalLink, Zap } from 'lucide-react';
import { createCoachChannel, COACH_POPOUT_FEATURES } from '../../help/coachChannel';
import { executeKcAction, undoKcAction } from './kcActions';
import activityService from '../../services/activityService';
import { KodahfyBadge } from '../common/KodahfyLogo';
import chatService from '../../services/chatService';
import insightsSettingsService from '../../services/insightsSettingsService';
import { useChatDock } from '../../contexts/ChatDockContext';

// ─────────────────────────────────────────────────────────────────────────────
// Tool-use display mapper. Turns a tool_use SSE event ({ name, input, query })
// into a human-readable status line shown under the streaming message. Web search
// keeps its existing phrasing; the custom KC tools get friendly strings so users
// don't see raw tool names like "get_open_positions".
// ─────────────────────────────────────────────────────────────────────────────
const TOOL_DISPLAY = {
  web_search:               (t) => `Searching the web${t?.query || t?.input?.query ? ` for "${t.query || t.input.query}"` : '...'}`,
  get_portfolio_summary:    () => 'Checking your portfolio summary...',
  get_open_positions:       (t) => t?.input?.symbol ? `Looking up ${t.input.symbol} positions...` : 'Fetching your open positions...',
  get_recent_closed_trades: () => 'Pulling up your recent trades...',
  get_system_performance:   (t) => t?.input?.system_name ? `Analysing ${t.input.system_name}...` : 'Reviewing your systems...',
  get_nav_summary:          () => 'Checking your NAV...',
  get_recent_transactions:  () => 'Scanning recent transactions...',
  get_journal_entries:      () => 'Reading your journal...',
  get_psychology_summary:   () => 'Reviewing your psychology data...',
  get_review_alerts:        () => 'Checking for items to review...',
  get_active_insights:      () => 'Reviewing flagged items...',
  get_trade_details:        (t) => t?.input?.batch_number
    ? `Fetching trade #${t.input.batch_number}...`
    : t?.input?.symbol
      ? `Fetching trade for ${t.input.symbol}...`
      : 'Fetching trade details...',
  get_benchmark_comparison: (t) => `Comparing to S&P 500 (${t?.input?.period || 'YTD'})...`,
  get_trading_plan:         () => 'Reading your trading plan...',
  get_trading_plan_history: () => 'Reviewing your plan history...',
  create_journal_entry:     (t) => t?.input?.title ? `Logging "${t.input.title}" to your journal...` : 'Saving to your journal...',
  create_psychology_entry:  (t) => t?.input?.entry_type === 'weekly' ? 'Saving your weekly review...' : 'Saving your daily check-in...',
  update_trade_stop:        (t) => t?.input?.symbol ? `Updating the stop on ${t.input.symbol}...` : 'Updating the trailing stop...',
  update_trade_meta:        (t) => t?.input?.symbol ? `Updating ${t.input.symbol}...` : 'Updating the trade...',
  append_trade_note:        (t) => t?.input?.symbol ? `Adding a note to ${t.input.symbol}...` : 'Adding a trade note...'
};

// Starter prompts shown under the welcome message on a fresh conversation —
// gives users (especially first-timers who don't know what to ask) a one-tap
// way in. Phrased to exercise KC's strengths: portfolio read + coaching.
const STARTER_PROMPTS = [
  "How's my risk looking right now?",
  'Review my last 10 closed trades',
  'Am I following my trading plan?',
  'Which of my systems is performing best?'
];

function formatToolUse(toolUse) {
  if (!toolUse) return null;
  const renderer = TOOL_DISPLAY[toolUse.name];
  if (renderer) return renderer(toolUse);
  // Unknown tool: fall back to a generic line using the tool's own name.
  return `Running ${String(toolUse.name).replace(/_/g, ' ')}...`;
}

/**
 * Simple markdown-like formatting for assistant messages.
 * Handles bold, italic, code blocks, inline code, and bullet lists.
 */
const formatMessage = (text) => {
  if (!text) return '';

  // Split by code blocks first
  const parts = text.split(/(```[\s\S]*?```)/g);

  return parts.map((part, i) => {
    // Code block
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).replace(/^\w+\n/, ''); // strip language tag
      return (
        <pre key={i} style={{
          backgroundColor: 'var(--bg-input)',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '0.8rem',
          overflowX: 'auto',
          margin: '8px 0',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {code.trim()}
        </pre>
      );
    }

    // Process inline formatting
    const lines = part.split('\n');
    return lines.map((line, j) => {
      // Bullet list items
      if (line.match(/^\s*[-*•]\s/)) {
        const content = line.replace(/^\s*[-*•]\s/, '');
        return (
          <div key={`${i}-${j}`} style={{ paddingLeft: '16px', position: 'relative', margin: '2px 0' }}>
            <span style={{ position: 'absolute', left: '4px' }}>•</span>
            {formatInline(content)}
          </div>
        );
      }

      // Numbered list items
      if (line.match(/^\s*\d+[.)]\s/)) {
        const match = line.match(/^(\s*\d+[.)]\s)(.*)/);
        return (
          <div key={`${i}-${j}`} style={{ paddingLeft: '16px', margin: '2px 0' }}>
            {formatInline(match ? match[2] : line)}
          </div>
        );
      }

      // Empty line = paragraph break
      if (line.trim() === '') {
        return <div key={`${i}-${j}`} style={{ height: '8px' }} />;
      }

      // Regular line
      return <div key={`${i}-${j}`} style={{ margin: '1px 0' }}>{formatInline(line)}</div>;
    });
  });
};

/** Format inline elements: bold, italic, inline code */
const formatInline = (text) => {
  if (!text) return text;

  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Inline code
    const codeMatch = remaining.match(/`([^`]+)`/);
    // Bold
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    // Italic
    const italicMatch = remaining.match(/\*([^*]+)\*/);

    // Find earliest match
    const matches = [
      codeMatch && { type: 'code', match: codeMatch },
      boldMatch && { type: 'bold', match: boldMatch },
      italicMatch && { type: 'italic', match: italicMatch }
    ].filter(Boolean).sort((a, b) => a.match.index - b.match.index);

    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = matches[0];
    const idx = first.match.index;

    // Text before match
    if (idx > 0) {
      parts.push(remaining.substring(0, idx));
    }

    if (first.type === 'code') {
      parts.push(
        <code key={key++} style={{
          backgroundColor: 'var(--bg-input)',
          padding: '1px 4px',
          borderRadius: '3px',
          fontSize: '0.85em',
          fontFamily: 'monospace'
        }}>
          {first.match[1]}
        </code>
      );
    } else if (first.type === 'bold') {
      parts.push(<strong key={key++}>{first.match[1]}</strong>);
    } else if (first.type === 'italic') {
      parts.push(<em key={key++}>{first.match[1]}</em>);
    }

    remaining = remaining.substring(idx + first.match[0].length);
  }

  return parts;
};

// CSS for animations
const styles = `
  @keyframes chatPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.4); }
    50% { box-shadow: 0 0 0 12px rgba(20, 184, 166, 0); }
  }

  @keyframes chatSlideUp {
    from { opacity: 0; transform: translateY(16px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes chatSlideDown {
    from { opacity: 1; transform: translateY(0) scale(1); }
    to { opacity: 0; transform: translateY(16px) scale(0.95); }
  }

  @keyframes dotBounce {
    0%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-4px); }
  }

  @keyframes cursorBlink {
    0%, 50% { opacity: 1; }
    50.01%, 100% { opacity: 0; }
  }

  .chat-panel-enter {
    animation: chatSlideUp 0.25s ease-out forwards;
  }

  .chat-panel-exit {
    animation: chatSlideDown 0.2s ease-in forwards;
  }
`;

const TypingIndicator = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 16px',
    maxWidth: '80px'
  }}>
    {[0, 1, 2].map(i => (
      <div
        key={i}
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: 'var(--text-muted)',
          animation: `dotBounce 1.2s infinite ${i * 0.15}s`
        }}
      />
    ))}
  </div>
);

const ChatAssistant = ({ fullPage = false }) => {
  const { isDocked, toggleDock, undock, dockedWidth, setDockedWidth, MIN_WIDTH, MAX_WIDTH } = useChatDock();
  // Beta-required — master kill-switch. true by default; flipped to false
  // if the user disabled the coach in Settings → Insights & AI. When false,
  // we render null below so the FAB + panel disappear entirely. Backend gate
  // (chatService.assertCoachEnabled) is the security layer; this is the UX.
  const [coachEnabled, setCoachEnabled] = useState(true);
  const [isOpen, setIsOpen] = useState(() => {
    if (fullPage) return true; // popped-out window is always open
    // Auto-open if docked on mount
    return localStorage.getItem('nvst_chat_docked') === 'true';
  });
  const navigate = useNavigate();
  // KC-B: where the user currently is. Captured at send time so KC can coach
  // on the actual screen ("I see you're on the Options tab") instead of blind.
  const location = useLocation();

  // ── Pop-out wiring (task #62) ─────────────────────────────────────────────
  // Main-window instance: broadcast the current screen; obey navigate posts
  // from a popped-out Coach. Pop-out instance: remember the main window's
  // last-known screen so context stays truthful, and send navigations back.
  const channelRef = useRef(null);
  const remoteScreenRef = useRef(null);
  useEffect(() => {
    const ch = createCoachChannel();
    channelRef.current = ch;
    if (!ch) return undefined;
    ch.onmessage = (ev) => {
      const msg = ev?.data;
      if (!msg) return;
      if (fullPage && msg.type === 'screen') {
        remoteScreenRef.current = { path: msg.path, search: msg.search || '' };
      }
      if (!fullPage && msg.type === 'navigate' && msg.route) {
        navigate(msg.route);
      }
    };
    return () => { try { ch.close(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPage]);
  useEffect(() => {
    if (fullPage) return; // the pop-out never advertises its own /coach route
    const ch = channelRef.current;
    if (ch) ch.postMessage({ type: 'screen', path: location.pathname, search: location.search });
  }, [fullPage, location.pathname, location.search]);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('kodahfy_chat_session') || null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [toolUse, setToolUse] = useState(null); // { name, query } when streaming tool use
  const [autoScroll, setAutoScroll] = useState(true); // pauses when user scrolls up
  const [usage, setUsage] = useState(null); // { messagesUsed, messagesLimit, messagesRemaining, resetDate }

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Resizable docked panel — drag state held in a ref so mousemove handlers
  // don't close over stale values. Drag writes the live width directly into
  // the CSS custom property `--kc-docked-width` (which Layout.jsx consumes
  // via `var(...)` in its layout-shift calcs), then commits to React state
  // on mouseup so localStorage + future re-renders see the new baseline.
  const dragRef = useRef({ active: false, startX: 0, startWidth: 0 });
  // Detect touch / coarse-pointer devices once at mount. We don't wire
  // touchstart handlers — per requirement 9, mobile/touch users keep the
  // existing bottom-sheet / floating behaviour and shouldn't see a drag
  // affordance they can't use.
  const isCoarsePointer = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(pointer: coarse)').matches;
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    // Panel is anchored to the right side of the viewport, so dragging the
    // handle LEFT (negative clientX delta) widens the panel.
    const delta = dragRef.current.startX - e.clientX;
    const raw = dragRef.current.startWidth + delta;
    const viewportCap = Math.max(MIN_WIDTH, Math.floor(window.innerWidth * 0.5));
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.min(raw, viewportCap)));
    document.documentElement.style.setProperty('--kc-docked-width', `${clamped}px`);
  }, [MIN_WIDTH, MAX_WIDTH]);

  const handleDragEnd = useCallback(() => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.classList.remove('kc-resizing');
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    // Commit the final CSS-var value into React state + localStorage. The
    // CSS var itself is already correct; the provider's effect that writes
    // the var from state will run again with the same value (no-op paint).
    const raw = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--kc-docked-width'),
      10
    );
    if (Number.isFinite(raw)) setDockedWidth(raw);
  }, [handleDragMove, setDockedWidth]);

  const handleDragStart = useCallback((e) => {
    // Left mouse button only; ignore middle/right clicks.
    if (e.button !== 0) return;
    dragRef.current = { active: true, startX: e.clientX, startWidth: dockedWidth };
    document.body.style.cursor = 'col-resize';
    // Prevent text selection from flickering under the drag cursor.
    document.body.style.userSelect = 'none';
    // Suppress the AppBar + main-content width/margin transitions while
    // resizing — Layout.jsx keeps those transitions on for smooth dock
    // toggle, but they'd chase the ~60fps CSS-var updates during drag and
    // land in a stale paint on release. See the matching rule in index.css.
    document.body.classList.add('kc-resizing');
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    e.preventDefault();
  }, [dockedWidth, handleDragMove, handleDragEnd]);

  // Double-click handle → snap back to minimum width. Small affordance for
  // users who widened too aggressively and want to restore the compact view
  // without fiddling with the drag.
  const handleDragReset = useCallback(() => {
    setDockedWidth(MIN_WIDTH);
  }, [setDockedWidth, MIN_WIDTH]);

  // Safety net: if the user un-docks or unmounts the component mid-drag, we
  // must not leave global listeners attached, the body cursor stuck, or the
  // kc-resizing class dangling (which would freeze dock-toggle transitions).
  useEffect(() => {
    return () => {
      if (dragRef.current.active) {
        dragRef.current.active = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.body.classList.remove('kc-resizing');
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      }
    };
  }, [handleDragMove, handleDragEnd]);

  // When docking, auto-open the chat panel
  useEffect(() => {
    if (isDocked && !isOpen) {
      setIsOpen(true);
      setIsClosing(false);
    }
  }, [isDocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Getting Started v2 — any "Ask KC" button anywhere can open the coach by
  // dispatching window CustomEvent('kodahfy:open-coach', { detail: { prompt } }).
  // Optional prompt pre-fills (and can auto-send) a starter question.
  useEffect(() => {
    const openCoach = (e) => {
      setIsOpen(true);
      setIsClosing(false);
      const prompt = e.detail?.prompt;
      if (prompt) setInputValue(prompt);
    };
    window.addEventListener('kodahfy:open-coach', openCoach);
    return () => window.removeEventListener('kodahfy:open-coach', openCoach);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom when messages change (pauses if user scrolled up)
  useEffect(() => {
    if (messagesEndRef.current && autoScroll) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, autoScroll]);

  // Detect user scrolling up — pause auto-scroll; resume when back at bottom
  const handleMessagesScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(atBottom);
  };

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Stop pulse animation after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => setHasAnimated(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  // KC-D: quiet nudge badge. When a nudge-worthy insight (critical/warning)
  // is active that this browser hasn't acknowledged, show a small count badge
  // on the coach launcher. Deliberately NOT a popup (JC: "those annoy
  // people"): the badge sits there until the user chooses to open the coach —
  // at which point the insights are marked seen locally, so each insight
  // nudges AT MOST ONCE per browser. KC's context already carries active
  // insights, so once opened, the coach leads with what it noticed on its own.
  const [nudgeCount, setNudgeCount] = useState(0);
  const nudgeIdsRef = useRef([]);

  const NUDGE_SEEN_KEY = 'kodahfy_nudge_seen';
  const readSeen = () => {
    try { return JSON.parse(localStorage.getItem(NUDGE_SEEN_KEY)) || {}; } catch (e) { return {}; }
  };

  // The amber count on the launcher was retired on 17 Oct 2026: what needs the
  // person is counted once, on the menu's "Needs you", and asked there as
  // questions with answers. A second count here said the same thing twice and
  // led to a chat instead of an answer. nudgeCount stays 0.

  // Opening the coach acknowledges the nudge: mark the flagged insights seen
  // so the badge never re-nags about them. The insights themselves stay active
  // (dismiss/resolve lives in the Insights UI) — only the DOT is consumed.
  const acknowledgeNudge = () => {
    if (nudgeIdsRef.current.length === 0) return;
    try {
      const seen = readSeen();
      const now = Date.now();
      for (const id of nudgeIdsRef.current) seen[id] = now;
      localStorage.setItem(NUDGE_SEEN_KEY, JSON.stringify(seen));
    } catch (e) { /* localStorage full/blocked — badge just shows again later */ }
    nudgeIdsRef.current = [];
    setNudgeCount(0);
  };

  // Beta-required — fetch the master kill-switch on mount + listen for
  // toggle events from InsightsAndAITab. Defaults to enabled if the fetch
  // fails (don't punish users for a transient settings read error). The
  // backend gate is the security layer; this is just the FAB-hide UX.
  useEffect(() => {
    let cancelled = false;
    insightsSettingsService.get()
      .then(data => {
        if (!cancelled && data) setCoachEnabled(data.chat_coach_enabled !== false);
      })
      .catch(() => { /* keep optimistic default */ });

    const handleToggle = (e) => {
      if (typeof e?.detail?.enabled === 'boolean') {
        setCoachEnabled(e.detail.enabled);
      }
    };
    window.addEventListener('kodahfy:coach-toggle', handleToggle);
    return () => {
      cancelled = true;
      window.removeEventListener('kodahfy:coach-toggle', handleToggle);
    };
  }, []);

  const getOrCreateSessionId = useCallback(() => {
    if (sessionId) return sessionId;
    const stored = localStorage.getItem('kodahfy_chat_session');
    if (stored) { setSessionId(stored); return stored; }
    const newId = crypto.randomUUID();
    setSessionId(newId);
    localStorage.setItem('kodahfy_chat_session', newId);
    return newId;
  }, [sessionId]);

  const handleOpen = async () => {
    setIsOpen(true);
    setIsClosing(false);
    // KC-D: capture BEFORE acknowledge clears it. If the badge is what brought
    // the user in, KC should lead — a badge that says "I noticed something" and
    // then shows the generic welcome is a broken promise.
    const openedFromNudge = nudgeCount > 0;
    acknowledgeNudge(); // opening the coach consumes the nudge dot

    // Load current session's messages from DB on first open
    if (messages.length === 0) {
      const sid = sessionId || localStorage.getItem('kodahfy_chat_session');
      let loadedHistory = false;
      if (sid) {
        try {
          const result = await chatService.getHistory(sid);
          // Seed usage bar from history response (always returned, even for empty sessions)
          if (result.data?.usage) setUsage(result.data.usage);
          if (result.data?.messages?.length > 0) {
            setMessages(result.data.messages);
            setSessionId(sid);
            loadedHistory = true;
          }
        } catch (err) {
          // History load failed — show welcome message
        }
      } else {
        // No session yet — fetch usage from server anyway so the bar shows on first render
        try {
          const result = await chatService.getHistory();
          if (result.data?.usage) setUsage(result.data.usage);
        } catch (err) { /* non-fatal */ }
      }

      if (!loadedHistory) {
        // No session or empty session — show welcome message
        setMessages([{
          role: 'assistant',
          content: "Hey! I'm your Kodahfy Coach. Ask me about your trades, performance, or anything on the platform. What's on your mind?"
        }]);
      }
    }

    // KC-D: opened via the nudge badge → KC speaks first. Auto-ask on the
    // user's behalf so the coach opens with what it flagged (its context
    // already carries the active insights). handleSend guards limit/loading;
    // the small delay lets the welcome/history render before KC's turn.
    if (openedFromNudge) {
      setTimeout(() => handleSend('What have you noticed that I should look at right now?'), 200);
    }
  };

  const handleClose = () => {
    // In docked mode, closing also undocks (restores normal layout)
    if (isDocked) {
      undock();
    }
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 200);
  };

  const handleReset = async () => {
    // Explicitly check server result before clearing UI. If the reset fails we
    // surface an error message in the chat instead of silently pretending it worked.
    let serverReset = false;
    let errorMessage = null;
    try {
      const result = await chatService.resetSession();
      if (result?.success !== false) {
        const newSid = result?.data?.sessionId;
        if (newSid) {
          setSessionId(newSid);
          localStorage.setItem('kodahfy_chat_session', newSid);
          serverReset = true;
        } else {
          errorMessage = 'Reset succeeded but no new session id was returned. Please refresh the page.';
        }
      } else {
        errorMessage = result?.message || 'Server could not reset the conversation. Please try again.';
      }
    } catch (err) {
      errorMessage = err?.response?.data?.message || err?.message || 'Could not reach the server to reset the conversation.';
    }

    if (serverReset) {
      setMessages([{
        role: 'assistant',
        content: 'Conversation reset. How can I help you?'
      }]);
    } else {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Couldn't reset the conversation: ${errorMessage}`,
        isError: true
      }]);
    }
  };

  // Draggable FAB: vertical position (px from bottom), persisted per browser.
  const [fabBottom, setFabBottom] = useState(() => {
    const saved = parseInt(localStorage.getItem('kodahfy_coach_fab_bottom'), 10);
    return Number.isFinite(saved) ? Math.max(16, Math.min(saved, window.innerHeight - 80)) : 24;
  });
  const fabDraggedRef = useRef(false);

  const handleFabPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const startY = e.clientY;
    const startBottom = fabBottom;
    let moved = false;
    const onMove = (ev) => {
      const delta = startY - ev.clientY; // dragging up increases bottom
      if (!moved && Math.abs(delta) < 6) return; // click, not drag, until 6px
      moved = true;
      fabDraggedRef.current = true;
      setFabBottom(Math.max(16, Math.min(startBottom + delta, window.innerHeight - 80)));
    };
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (moved) {
        const delta = startY - ev.clientY;
        const finalBottom = Math.max(16, Math.min(startBottom + delta, window.innerHeight - 80));
        setFabBottom(finalBottom);
        localStorage.setItem('kodahfy_coach_fab_bottom', String(Math.round(finalBottom)));
      }
      // fabDraggedRef stays true through the click event that follows a drag
      // release; the click handler consumes and resets it.
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // KC-DO (#63): patch one offer card's state inside a message.
  const patchOffer = (msgIndex, key, patch) => {
    setMessages(prev => {
      const next = [...prev];
      const msg = next[msgIndex];
      if (!msg?.actions) return prev;
      next[msgIndex] = {
        ...msg,
        actions: msg.actions.map(a => (a.key === key ? { ...a, ...patch } : a))
      };
      return next;
    });
  };

  // Execute an offered action. The click is the consent — everything runs
  // through the same authenticated endpoints the UI buttons use.
  const runOfferAction = async (msgIndex, key) => {
    const action = messages[msgIndex]?.actions?.find(a => a.key === key);
    patchOffer(msgIndex, key, { status: 'running' });
    try {
      const result = await executeKcAction(key, action?.params);
      // The ledger (11 Sep 2026): a coach action is recorded the moment it
      // runs, with its own undo, so it outlives the chat window.
      let ledgerId = null;
      try {
        ledgerId = await activityService.recordCoachAction({
          summary: `${action?.label || key}${action?.reason ? ` — ${action.reason}` : ''}`,
          details: { key, result: result.message },
          undo: result.undoToken ? { key, token: result.undoToken } : null,
          link: null
        });
      } catch (_) { /* the ledger is a record, never a blocker */ }
      patchOffer(msgIndex, key, {
        status: 'done',
        resultMessage: result.message,
        undoToken: result.undoToken || null,
        ledgerId
      });
    } catch (err) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message;
      patchOffer(msgIndex, key, {
        status: 'error',
        resultMessage: apiMsg || 'That didn’t work — you can try again or do it from the page itself.'
      });
    }
  };

  const runOfferUndo = async (msgIndex, key) => {
    const msg = messages[msgIndex];
    const action = msg?.actions?.find(a => a.key === key);
    if (!action?.undoToken) return;
    patchOffer(msgIndex, key, { undoStatus: 'running' });
    try {
      const result = await undoKcAction(key, action.undoToken);
      if (action.ledgerId) activityService.markUndone(action.ledgerId).catch(() => {});
      patchOffer(msgIndex, key, { undoStatus: 'done', undoMessage: result.message });
    } catch (err) {
      const apiMsg = err?.response?.data?.message || err?.message;
      patchOffer(msgIndex, key, { undoStatus: 'idle', undoMessage: null, resultMessage: `${action.resultMessage} (Undo failed: ${apiMsg})` });
    }
  };

  const handleSend = async (overrideText) => {
    // overrideText is a string when called from a starter-prompt chip; when
    // used as an onClick handler the first arg is the event object, so only
    // honour a genuine string and otherwise fall back to the input field.
    const message = (typeof overrideText === 'string' ? overrideText : inputValue).trim();
    if (!message || isLoading || atLimit) return;

    const sid = getOrCreateSessionId();
    setInputValue('');
    // Reset textarea height after send
    if (inputRef.current) inputRef.current.style.height = 'auto';

    // Append user message + empty streaming assistant message
    setMessages(prev => [
      ...prev,
      { role: 'user', content: message },
      { role: 'assistant', content: '', streaming: true }
    ]);
    setIsLoading(true);
    setToolUse(null);
    setAutoScroll(true); // re-enable auto-scroll on new send

    // KC-B: screen context rides along so KC knows where the user is. The
    // backend treats it as an untrusted hint (allowlist-validated), never as
    // instructions.
    const screen = fullPage
      ? (remoteScreenRef.current || { path: location.pathname, search: location.search })
      : { path: location.pathname, search: location.search };

    await chatService.streamMessage(message, sid, screen, {
      onDelta: (text) => {
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.streaming) {
            next[next.length - 1] = { ...last, content: (last.content || '') + text };
          }
          return next;
        });
      },
      onToolUse: (data) => setToolUse(data),
      // KC-A: attach a navigate action to the current assistant message so a
      // "Take me there" button renders under it (deduped by route).
      // KC-DO (#63): 'offer' actions render as a "Do it" card instead — the
      // click is what executes, via the same endpoints as the UI buttons.
      onAction: (data) => {
        const isNavigate = data.action === 'navigate' && data.route;
        const isOffer = data.action === 'offer' && data.key;
        if (!isNavigate && !isOffer) return;
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          const actions = last.actions || [];
          if (isNavigate) {
            if (actions.some(a => a.route === data.route)) return prev;
            next[next.length - 1] = {
              ...last,
              actions: [...actions, { route: data.route, label: data.label, reason: data.reason }]
            };
          } else {
            if (actions.some(a => a.key === data.key)) return prev;
            next[next.length - 1] = {
              ...last,
              actions: [...actions, {
                key: data.key, label: data.label, summary: data.summary,
                reason: data.reason, params: data.params || null, status: 'idle'
              }]
            };
          }
          return next;
        });
      },
      onUsage: (data) => setUsage({
        messagesUsed: data.messagesUsed,
        messagesLimit: data.messagesLimit,
        messagesRemaining: data.messagesRemaining,
        resetDate: data.resetDate
      }),
      onDone: () => {
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.streaming) next[next.length - 1] = { ...last, streaming: false };
          return next;
        });
        setStreamingDone();
      },
      onError: (errMsg, errBody) => {
        // Monthly limit hit — replace the empty streaming bubble with a friendly message
        // and mark usage as exhausted so the input disables.
        if (errBody?.monthlyLimit) {
          setUsage(prev => prev ? { ...prev, messagesUsed: prev.messagesLimit, messagesRemaining: 0 } : prev);
        }
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          const errorText = errMsg || 'Failed to get a response. Please try again.';
          if (last?.streaming) {
            const hasContent = (last.content || '').length > 0;
            next[next.length - 1] = {
              ...last,
              content: hasContent ? last.content : errorText,
              streaming: false,
              isError: !hasContent
            };
            // If we got partial content, append an error note
            if (hasContent) {
              next.push({ role: 'assistant', content: `(Stream interrupted: ${errorText})`, isError: true });
            }
          } else {
            next.push({ role: 'assistant', content: errorText, isError: true });
          }
          return next;
        });
        setStreamingDone();
      }
    });
  };

  // Derived state for usage bar styling
  const usagePct = usage ? Math.min(100, (usage.messagesUsed / Math.max(1, usage.messagesLimit)) * 100) : 0;
  const atLimit = !!usage && usage.messagesRemaining <= 0;
  const usageBarColor = usagePct >= 100 ? '#ef4444' : usagePct >= 80 ? '#f59e0b' : 'var(--teal)';
  const formatResetDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const setStreamingDone = () => {
    setToolUse(null);
    setIsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Beta-required — when the user has disabled the coach in Settings, hide
  // the entire surface (FAB + panel). Backend rejects /chat/* with 403 too,
  // so a stale tab can't sneak through.
  if (!coachEnabled) return null;

  return (
    <>
      {/* Inject animation styles */}
      <style>{styles}</style>

      {/* Chat Panel */}
      {isOpen && (
        <div
          className={(fullPage || isDocked) ? '' : (isClosing ? 'chat-panel-exit' : 'chat-panel-enter')}
          style={fullPage ? {
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'var(--bg-card)',
          } : isDocked ? {
            position: 'fixed',
            // Full-height panel (top:0 not 64) — the teal header now fills
            // what was previously a dead 360×64 white corner in the top-right
            // of the viewport. Safe because Layout.jsx's AppBar has
            // `right: isChatDocked ? dockedWidthVar : 0`, so the AppBar stops
            // at the chat panel's left edge and the two never horizontally
            // overlap. Panel's z-index (1200) > AppBar's default (1100) so
            // the 1px border seam paints cleanly either way.
            top: 0,
            right: 0,
            // Width follows the CSS custom property so the panel can be
            // resized live during a drag without triggering React re-renders.
            // Committed baseline from React state is the fallback if the
            // var isn't set yet.
            width: `var(--kc-docked-width, ${dockedWidth}px)`,
            height: '100vh',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1200,
            backgroundColor: 'var(--bg-card)',
            borderLeft: '1px solid var(--border)',
            boxShadow: 'none',
            // No transition while docked — it would lag behind the drag.
            // The Layout's main content has its own transition; when the
            // drag commits via setDockedWidth, the var is already the new
            // value so no transition is needed here either.
          } : {
            position: 'fixed',
            bottom: '88px',
            right: '24px',
            // Beta-required fix — previously hardcoded 400px caused the
            // floating panel to overflow the viewport horizontally on phones.
            // MobileNotice covers <768px but users can "Continue anyway",
            // and tablets sit in the 600-767px window. Clamp width to never
            // exceed (viewport - right_inset - safe_left_margin):
            //   min(400px, calc(100vw - 48px))
            // On desktop the user sees the original 400px; on a 360px phone
            // the panel shrinks to 312px instead of clipping off-screen.
            width: 'min(400px, calc(100vw - 48px))',
            height: '500px',
            maxHeight: 'calc(100vh - 120px)',
            minHeight: '300px',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1300,
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}
        >
          {/* Drag handle — docked mode + mouse/pointer devices only.
              6px-wide hit zone on the panel's left edge. Visual affordance
              is a vertical stack of 4 small dots centred on the handle —
              the pattern users recognise from Slack / Notion / VS Code. Dots
              sit muted-grey at rest so the chrome stays unobtrusive, then
              brighten teal on hover so the handle becomes obvious the moment
              the mouse lands in the neighbourhood. Double-click resets to
              MIN_WIDTH. */}
          {isDocked && !isCoarsePointer && (
            <div
              role="separator"
              aria-label="Resize chat panel (drag) or double-click to reset"
              className="kc-drag-handle"
              onMouseDown={handleDragStart}
              onDoubleClick={handleDragReset}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                width: '6px',
                cursor: 'col-resize',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* 4-dot stack. Inline element so flex centres it both axes. */}
              <div
                className="kc-drag-handle-dots"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  pointerEvents: 'none' // clicks pass through to the parent hit zone
                }}
              >
                {[0, 1, 2, 3].map(i => (
                  <span
                    key={i}
                    className="kc-drag-handle-dot"
                    style={{
                      display: 'block',
                      width: '3px',
                      height: '3px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(148, 163, 184, 0.55)', // muted slate-grey at rest
                      transition: 'background-color 0.15s ease-in-out'
                    }}
                  />
                ))}
              </div>
              <style>{`
                .kc-drag-handle:hover .kc-drag-handle-dot {
                  background-color: rgba(20, 184, 166, 0.9);
                }
              `}</style>
            </div>
          )}

          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #14B8A6, #0D9488)',
            color: 'white',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <KodahfyBadge size={22} variant="light" />
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Kodahfy Coach</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {!fullPage && (
                <button
                  onClick={() => {
                    window.open('/coach', 'kodahfyCoach', COACH_POPOUT_FEATURES);
                    handleClose();
                  }}
                  title="Pop out into its own window"
                  style={{
                    background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
                    cursor: 'pointer', padding: '4px', borderRadius: '4px',
                    display: 'flex', alignItems: 'center', transition: 'color 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
                >
                  <ExternalLink size={15} />
                </button>
              )}
              <button
                onClick={handleReset}
                title="Reset conversation"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'white'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
              >
                <RotateCcw size={16} />
              </button>
              <button
                onClick={() => {
                  if (!isDocked) {
                    // Docking — keep chat open, switch to docked mode
                    toggleDock();
                  } else {
                    // Undocking — switch to floating mode, keep chat open
                    toggleDock();
                  }
                }}
                title={isDocked ? 'Undock chat' : 'Dock chat to side'}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'white'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
              >
                {isDocked ? <PanelRightClose size={16} /> : <PanelRight size={16} />}
              </button>
              <button
                onClick={handleClose}
                title="Close chat"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'white'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            backgroundColor: 'var(--bg-page)'
          }}>
            {messages.map((msg, index) => {
              // Skip the bubble wrapper for assistant messages still waiting
              // on the first streamed token. The chat service inserts an
              // empty placeholder ({ role: 'assistant', content: '', streaming
              // : true }) the moment a request is dispatched so subsequent
              // deltas have somewhere to append; rendering that placeholder
              // produces a visibly empty bubble above the typing dots in the
              // gap between send and first token. The typing indicator below
              // already covers that loading state — defer to it until the
              // first character arrives, then this guard releases and the
              // streaming bubble takes over.
              if (msg.streaming && !msg.content) return null;
              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{
                    maxWidth: '85%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user'
                      ? '14px 14px 4px 14px'
                      : '14px 14px 14px 4px',
                    backgroundColor: msg.role === 'user'
                      ? 'var(--teal)'
                      : msg.isError
                        ? 'var(--red-bg)'
                        : 'var(--bg-card)',
                    color: msg.role === 'user'
                      ? 'white'
                      : msg.isError
                        ? 'var(--red)'
                        : 'var(--text-primary)',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                    border: msg.role === 'user' ? 'none' : '1px solid var(--border)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>
                    {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
                    {msg.streaming && msg.content && (
                      <span style={{
                        display: 'inline-block',
                        width: '2px',
                        height: '14px',
                        marginLeft: '2px',
                        verticalAlign: 'middle',
                        backgroundColor: 'var(--text-primary)',
                        animation: 'cursorBlink 1s step-end infinite'
                      }} />
                    )}
                    {/* KC-A: "Take me there" navigation buttons.
                        KC-DO (#63): offer cards — the click executes. */}
                    {msg.role === 'assistant' && msg.actions?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                        {msg.actions.map((a, i) => (a.key ? (
                          <div key={i} style={{
                            width: '100%', padding: '10px 12px', borderRadius: '10px',
                            border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)'
                          }}>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: 1.45 }}>
                              {a.summary}
                            </div>
                            {a.status === 'done' ? (
                              <div style={{ fontSize: '0.8rem', color: 'var(--teal)', fontWeight: 600 }}>
                                ✓ {a.resultMessage}
                                {a.undoToken && a.undoStatus !== 'done' && (
                                  <button
                                    onClick={() => runOfferUndo(index, a.key)}
                                    disabled={a.undoStatus === 'running'}
                                    style={{
                                      marginLeft: '10px', padding: '2px 8px', borderRadius: '6px',
                                      border: '1px solid var(--border)', backgroundColor: 'transparent',
                                      color: 'var(--text-secondary)', fontSize: '0.75rem', cursor: 'pointer'
                                    }}
                                  >
                                    {a.undoStatus === 'running' ? 'Undoing…' : 'Undo'}
                                  </button>
                                )}
                                {a.undoStatus === 'done' && (
                                  <span style={{ marginLeft: '10px', color: 'var(--text-secondary)', fontWeight: 400 }}>
                                    {a.undoMessage}
                                  </span>
                                )}
                              </div>
                            ) : a.status === 'error' ? (
                              <div style={{ fontSize: '0.8rem', color: 'var(--red)' }}>
                                {a.resultMessage}
                                <button
                                  onClick={() => runOfferAction(index, a.key)}
                                  style={{
                                    marginLeft: '10px', padding: '2px 8px', borderRadius: '6px',
                                    border: '1px solid var(--border)', backgroundColor: 'transparent',
                                    color: 'var(--text-secondary)', fontSize: '0.75rem', cursor: 'pointer'
                                  }}
                                >
                                  Try again
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => runOfferAction(index, a.key)}
                                disabled={a.status === 'running'}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                                  padding: '7px 12px', borderRadius: '8px',
                                  cursor: a.status === 'running' ? 'default' : 'pointer',
                                  border: 'none', backgroundColor: 'var(--teal)', color: 'white',
                                  fontSize: '0.8rem', fontWeight: 600,
                                  opacity: a.status === 'running' ? 0.7 : 1
                                }}
                              >
                                {a.status === 'running'
                                  ? 'Doing it…'
                                  : (a.reason ? a.reason.charAt(0).toUpperCase() + a.reason.slice(1) : a.label)}
                                {a.status !== 'running' && <Zap size={14} />}
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            key={i}
                            onClick={() => {
                              if (fullPage && channelRef.current && window.opener) {
                                channelRef.current.postMessage({ type: 'navigate', route: a.route });
                                try { window.opener.focus(); } catch { /* noop */ }
                                return;
                              }
                              navigate(a.route);
                              if (!fullPage && !isDocked) handleClose();
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              padding: '7px 12px', borderRadius: '8px', cursor: 'pointer',
                              border: 'none', backgroundColor: 'var(--teal)', color: 'white',
                              fontSize: '0.8rem', fontWeight: 600
                            }}
                          >
                            {a.reason ? a.reason.charAt(0).toUpperCase() + a.reason.slice(1) : `Go to ${a.label}`}
                            <ArrowRight size={14} />
                          </button>
                        )))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Starter prompts — only on a fresh conversation (just the welcome
                message), not while loading or at the monthly limit. One tap
                sends the prompt straight to KC. */}
            {messages.length === 1 && messages[0].role === 'assistant' && !isLoading && !atLimit && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                {STARTER_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => handleSend(prompt)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '14px',
                      border: '1px solid var(--border)',
                      backgroundColor: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      lineHeight: 1.3
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
            {/* Typing indicator — only when loading and no streaming content yet */}
            {isLoading && !messages.some(m => m.streaming && m.content) && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '14px 14px 14px 4px',
                  border: '1px solid var(--border)'
                }}>
                  <TypingIndicator />
                </div>
              </div>
            )}
            {/* Tool use indicator — web_search OR any of the KC custom tools */}
            {toolUse && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                fontStyle: 'italic'
              }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--teal)',
                  animation: 'dotBounce 1.2s infinite'
                }} />
                {formatToolUse(toolUse)}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            padding: '12px 16px',
            borderTop: '1px solid var(--border)',
            backgroundColor: 'var(--bg-card)',
            flexShrink: 0
          }}>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={e => {
                // Auto-grow up to ~4 lines (100px)
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
              }}
              placeholder={atLimit
                ? `Monthly limit reached. Resets ${formatResetDate(usage.resetDate)}.`
                : 'Ask about Kodahfy...'}
              disabled={isLoading || atLimit}
              rows={1}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-input)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                outline: 'none',
                fontFamily: 'inherit',
                resize: 'none',
                maxHeight: '100px',
                overflowY: 'auto',
                lineHeight: 1.4,
                transition: 'border-color 0.15s'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--teal)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading || atLimit}
              title={atLimit ? 'Monthly limit reached' : 'Send message'}
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: (!inputValue.trim() || isLoading || atLimit)
                  ? 'var(--bg-input)'
                  : 'var(--teal)',
                color: (!inputValue.trim() || isLoading || atLimit)
                  ? 'var(--text-muted)'
                  : 'white',
                cursor: (!inputValue.trim() || isLoading || atLimit) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.15s'
              }}
            >
              <Send size={16} />
            </button>
          </div>

          {/* Usage bar — messages used this month. Hidden until 80%: showing
              "3/300" tells a new user this is rationed before they've formed
              a habit, and no real user has ever come near the ceiling. The
              meter appears only when it's genuinely close, as a warning. */}
          {usage && usagePct >= 80 && (
            <div style={{
              padding: '6px 16px 8px',
              borderTop: '1px solid var(--border)',
              backgroundColor: 'var(--bg-card)',
              flexShrink: 0
            }}>
              <div style={{
                fontSize: '0.6875rem',
                display: 'flex',
                justifyContent: 'space-between',
                opacity: atLimit ? 1 : 0.65,
                color: atLimit ? '#ef4444' : 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                <span>{usage.messagesUsed} / {usage.messagesLimit} messages this month</span>
                {atLimit && usage.resetDate && <span>Resets {formatResetDate(usage.resetDate)}</span>}
              </div>
              <div style={{
                height: '3px',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${usagePct}%`,
                  background: usageBarColor,
                  borderRadius: '2px',
                  transition: 'width 0.3s ease, background 0.2s'
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Chat Button — hidden when docked or popped out.
          Draggable VERTICALLY along the right edge (JC 2026-08-12: it was
          sitting on top of sticky Save bars). Hold-and-drag slides it; a
          plain click still opens. Position persists per browser. Kept on the
          right edge on purpose — free 2D dragging invites lost bubbles. */}
      {!fullPage && !isDocked && <button
        onClick={(e) => {
          if (fabDraggedRef.current) { fabDraggedRef.current = false; return; }
          (isOpen ? handleClose : handleOpen)(e);
        }}
        onPointerDown={handleFabPointerDown}
        title={isOpen ? 'Close chat' : (nudgeCount > 0 ? 'Kodahfy Coach — I noticed something worth a look' : 'Kodahfy Coach — drag up or down to move me')}
        style={{
          position: 'fixed',
          bottom: `${fabBottom}px`,
          right: '24px',
          touchAction: 'none',
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          border: 'none',
          backgroundColor: 'var(--teal)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
          boxShadow: '0 4px 12px rgba(20, 184, 166, 0.35)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          animation: (!hasAnimated && !isOpen) ? 'chatPulse 2s ease-in-out 3' : 'none'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(20, 184, 166, 0.45)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(20, 184, 166, 0.35)';
        }}
      >
        {isOpen ? <X size={22} /> : <KodahfyBadge size={28} variant="light" />}
      </button>}

      {/* Mobile responsive override */}
      <style>{`
        @media (max-width: 600px) {
          .chat-panel-enter, .chat-panel-exit {
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            height: 70vh !important;
            border-radius: 16px 16px 0 0 !important;
          }
        }
      `}</style>
    </>
  );
};

export default ChatAssistant;
